import { useState, useRef, useCallback, useEffect } from "react";

const DEFAULT_FILTERS = {
  brightness: 100,
  contrast: 100,
  saturate: 100,
  hue: 0,
  warmth: 0,    // maps to sepia + hue-rotate combo
  blur: 0,
  opacity: 100,
};

const DEFAULT_POSITION = { x: 50, y: 50 }; // object-position percentages

// Build CSS filter string from filter values
export const buildFilterCSS = (filters = {}) => {
  const f = { ...DEFAULT_FILTERS, ...filters };
  const parts = [];

  if (f.brightness !== 100) parts.push(`brightness(${f.brightness}%)`);
  if (f.contrast !== 100) parts.push(`contrast(${f.contrast}%)`);
  if (f.saturate !== 100) parts.push(`saturate(${f.saturate}%)`);
  if (f.hue !== 0) parts.push(`hue-rotate(${f.hue}deg)`);
  if (f.warmth > 0) {
    parts.push(`sepia(${Math.min(f.warmth, 40)}%)`);
    parts.push(`hue-rotate(-${Math.round(f.warmth * 0.3)}deg)`);
  }
  if (f.blur > 0) parts.push(`blur(${f.blur}px)`);
  if (f.opacity !== 100) parts.push(`opacity(${f.opacity}%)`);

  return parts.length > 0 ? parts.join(" ") : "none";
};

export const buildPositionCSS = (position) => {
  const p = { ...DEFAULT_POSITION, ...position };
  return `${p.x}% ${p.y}%`;
};

const SLIDERS = [
  { key: "brightness", label: "Brightness", min: 20, max: 200, icon: "☀️" },
  { key: "contrast", label: "Contrast", min: 20, max: 200, icon: "◐" },
  { key: "saturate", label: "Saturation", min: 0, max: 200, icon: "🎨" },
  { key: "hue", label: "Hue", min: -180, max: 180, icon: "🌈" },
  { key: "warmth", label: "Warmth", min: 0, max: 60, icon: "🔥" },
  { key: "blur", label: "Blur", min: 0, max: 10, step: 0.5, icon: "💧" },
  { key: "opacity", label: "Opacity", min: 10, max: 100, icon: "👁️" },
];

const MediaEditor = ({ media, onSave, onClose }) => {
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...(media.filters || {}) });
  const [position, setPosition] = useState({ ...DEFAULT_POSITION, ...(media.position || {}) });
  const [isDragging, setIsDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState("editor"); // editor | hero | profile
  const previewRef = useRef(null);

  const isVideo = media.type === "video";

  const filterCSS = buildFilterCSS(filters);
  const positionCSS = buildPositionCSS(position);

  const handleSlider = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: parseFloat(value) }));
  };

  const resetFilters = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setPosition({ ...DEFAULT_POSITION });
  };

  // Drag to reposition
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !previewRef.current) return;
    const rect = previewRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setPosition({ x: Math.round(x), y: Math.round(y) });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(media._id, { filters, position });
    setSaving(false);
  };

  const hasChanges =
    JSON.stringify(filters) !== JSON.stringify({ ...DEFAULT_FILTERS, ...(media.filters || {}) }) ||
    JSON.stringify(position) !== JSON.stringify({ ...DEFAULT_POSITION, ...(media.position || {}) });

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex">
      {/* Left: Preview */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 min-w-0">
        {/* Preview mode tabs */}
        <div className="flex gap-2 mb-4">
          {[
            { id: "editor", label: "Editor" },
            { id: "hero", label: "Hero Preview" },
            { id: "profile", label: "Card Preview" },
          ].map(m => (
            <button key={m.id}
              onClick={() => setPreviewMode(m.id)}
              className={`px-3 py-1.5 text-xs font-body rounded-lg transition-colors ${
                previewMode === m.id
                  ? "bg-purple-600 text-white"
                  : "bg-gray-800 text-gray-400 hover:text-white"
              }`}
            >{m.label}</button>
          ))}
        </div>

        {/* Editor preview — full view with drag */}
        {previewMode === "editor" && (
          <div
            ref={previewRef}
            className={`relative overflow-hidden rounded-xl border-2 ${
              isDragging ? "border-gold cursor-crosshair" : "border-gray-700 cursor-grab"
            }`}
            style={{ width: "100%", maxWidth: "700px", aspectRatio: "16/9" }}
            onMouseDown={handleMouseDown}
          >
            {isVideo ? (
              <video
                src={media.url}
                className="w-full h-full object-cover"
                style={{ filter: filterCSS, objectPosition: positionCSS }}
                autoPlay muted loop playsInline
              />
            ) : (
              <img
                src={media.url}
                alt={media.originalName}
                className="w-full h-full object-cover"
                style={{ filter: filterCSS, objectPosition: positionCSS }}
                draggable={false}
              />
            )}
            {/* Crosshair */}
            <div className="absolute pointer-events-none" style={{
              left: `${position.x}%`, top: `${position.y}%`,
              transform: "translate(-50%, -50%)",
            }}>
              <div className="w-6 h-6 border-2 border-gold rounded-full shadow-lg shadow-gold/30" />
              <div className="absolute top-1/2 left-1/2 w-1 h-1 bg-gold rounded-full -translate-x-1/2 -translate-y-1/2" />
            </div>
            <p className="absolute bottom-2 left-2 text-white/50 text-xs font-body">
              Drag to reposition focal point
            </p>
          </div>
        )}

        {/* Hero preview — simulates homepage */}
        {previewMode === "hero" && (
          <div className="w-full max-w-[800px] relative overflow-hidden rounded-xl border border-gray-700"
            style={{ height: "400px" }}>
            {isVideo ? (
              <video src={media.url} className="w-full h-full object-cover"
                style={{ filter: filterCSS, objectPosition: positionCSS }}
                autoPlay muted loop playsInline />
            ) : (
              <img src={media.url} alt="" className="w-full h-full object-cover"
                style={{ filter: filterCSS, objectPosition: positionCSS }} draggable={false} />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40" />
            <div className="absolute bottom-16 left-6 z-10">
              <h1 className="text-3xl font-bold text-white font-heading">Exo Entertainment</h1>
              <p className="text-sm text-gray-300 font-body mt-1">Calgary's Premier Private Entertainment</p>
            </div>
            <div className="absolute bottom-5 left-6 z-10">
              <div className="px-6 py-2 bg-gold text-black font-bold text-sm rounded-full">Book Now</div>
            </div>
          </div>
        )}

        {/* Profile card preview */}
        {previewMode === "profile" && (
          <div className="w-64 bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 shadow-xl">
            <div className="h-72 overflow-hidden">
              {isVideo ? (
                <video src={media.url} className="w-full h-full object-cover"
                  style={{ filter: filterCSS, objectPosition: positionCSS }}
                  autoPlay muted loop playsInline />
              ) : (
                <img src={media.url} alt="" className="w-full h-full object-cover"
                  style={{ filter: filterCSS, objectPosition: positionCSS }} draggable={false} />
              )}
            </div>
            <div className="p-4 text-center">
              <h3 className="text-white font-heading text-lg">{media.personaId?.name || "Performer"}</h3>
              <p className="text-gray-400 text-xs font-body mt-1">Elegance personified</p>
            </div>
          </div>
        )}
      </div>

      {/* Right: Controls */}
      <div className="w-80 bg-gray-950 border-l border-gray-800 flex flex-col overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div>
            <h3 className="text-white font-heading text-lg">Edit Media</h3>
            <p className="text-gray-500 text-xs font-body truncate">{media.originalName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl leading-none">&times;</button>
        </div>

        {/* Position */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-300 text-xs font-body uppercase tracking-wider">Position</span>
            <span className="text-gray-500 text-xs font-body">{position.x}%, {position.y}%</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-gray-500 text-xs font-body">X</label>
              <input type="range" min="0" max="100" value={position.x}
                onChange={e => setPosition(p => ({ ...p, x: parseInt(e.target.value) }))}
                className="w-full accent-purple-500" />
            </div>
            <div>
              <label className="text-gray-500 text-xs font-body">Y</label>
              <input type="range" min="0" max="100" value={position.y}
                onChange={e => setPosition(p => ({ ...p, y: parseInt(e.target.value) }))}
                className="w-full accent-purple-500" />
            </div>
          </div>
        </div>

        {/* Color sliders */}
        <div className="p-4 flex-1">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-300 text-xs font-body uppercase tracking-wider">Color Correction</span>
            <button onClick={resetFilters} className="text-purple-400 hover:text-purple-300 text-xs font-body">
              Reset All
            </button>
          </div>

          <div className="space-y-4">
            {SLIDERS.map(s => {
              const val = filters[s.key];
              const isDefault = val === (DEFAULT_FILTERS[s.key] ?? 0);
              return (
                <div key={s.key}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-gray-400 text-xs font-body">
                      {s.icon} {s.label}
                    </span>
                    <span className={`text-xs font-body tabular-nums ${isDefault ? "text-gray-600" : "text-white"}`}>
                      {s.key === "hue" ? `${val}°` : s.key === "blur" ? `${val}px` : `${val}%`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={s.min} max={s.max} step={s.step || 1}
                      value={val}
                      onChange={e => handleSlider(s.key, e.target.value)}
                      className="flex-1 accent-purple-500"
                    />
                    <button
                      onClick={() => handleSlider(s.key, DEFAULT_FILTERS[s.key])}
                      className={`text-xs px-1 ${isDefault ? "text-gray-700" : "text-gray-400 hover:text-white"}`}
                      disabled={isDefault}
                    >↺</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-gray-800 space-y-2">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="w-full py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-body font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : hasChanges ? "Save Changes" : "No Changes"}
          </button>
          <button
            onClick={onClose}
            className="w-full py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 font-body text-sm rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaEditor;
